import React from 'react'
import { Alert, Button } from 'react-bootstrap'
import { withTranslation } from 'react-i18next'
import Icon from '@mdi/react'
import { mdiReload } from '@mdi/js'

// Tras un despliegue, un cliente puede seguir solicitando chunks de una build anterior.
// sessionStorage mantiene el limite por pestaña incluso después de recargar.
const RETRY_KEY = 'core_chunk_reload_at'
const RETRY_INTERVAL = 5 * 60 * 1000

// Solo recuperamos fallos de carga de JavaScript/CSS, no cualquier error de la vista.
export const isChunkLoadError = (error) => Boolean(error && (
  error.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk .+ failed/i.test(error.message || '')
))

export const recoverChunkLoad = (error, browser = window) => {
  if (!isChunkLoadError(error)) return false

  try {
    const previousAttempt = Number(browser.sessionStorage.getItem(RETRY_KEY))
    const now = Date.now()
    if (previousAttempt && now - previousAttempt < RETRY_INTERVAL) return false

    // Guardamos el intento antes de recargar para evitar bucles si el chunk sigue ausente.
    // Recargar no borra el usuario autenticado guardado en sessionStorage.
    browser.sessionStorage.setItem(RETRY_KEY, String(now))
    browser.location.reload()
    return true
  } catch {
    // Si el navegador bloquea el almacenamiento, dejamos disponible el reintento manual.
    return false
  }
}

// LoadComponent envuelve las rutas React.lazy con este boundary: Suspense solo maneja
// la espera, no el rechazo del import. React requiere una clase para capturar ese error.
export class ChunkLoadBoundary extends React.Component {
  state = { error: null }

  static getDerivedStateFromError (error) {
    return { error }
  }

  componentDidCatch (error) {
    recoverChunkLoad(error)
  }

  render () {
    const { error } = this.state
    if (!error) return this.props.children
    // Los errores ajenos a chunks siguen propagandose; no deben quedar ocultos aqui.
    if (!isChunkLoadError(error)) throw error

    // Si no se puede recuperar o el fallo se repite, evitamos dejar la pantalla en blanco.
    const { t } = this.props
    return (
      <Alert variant='danger' role='alert' className='m-3'>
        <p>{t('Error al cargar la pantalla.')}</p>
        <Button variant='outline-danger' onClick={() => window.location.reload()}>
          <Icon path={mdiReload} size={0.8} className='me-1' />
          {t('Reintentar')}
        </Button>
      </Alert>
    )
  }
}

export default withTranslation()(ChunkLoadBoundary)
